import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { adminFieldLibraryService } from '../../services/admin.fieldLibrary.service'
import type { ApplicationField } from '@/types'

const schema = z.object({
  fieldId: z
    .string()
    .min(1, 'Field ID is required')
    .regex(/^[a-z0-9-]+$/, 'Field ID may only contain lowercase letters, numbers, and hyphens'),
  category: z.string(),
})

type FormValues = z.infer<typeof schema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  field: ApplicationField | null
  onSaved: () => void
}

export function SaveToLibraryDialog({ open, onOpenChange, field, onSaved }: Props) {
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      fieldId: field ? slugify(field.label) : '',
      category: 'General',
    },
  })

  async function onSubmit(values: FormValues) {
    if (!field) return
    setError(null)
    try {
      await adminFieldLibraryService.create({
        fieldId: values.fieldId,
        type: field.type,
        label: field.label,
        description: field.description ?? '',
        placeholder: field.placeholder ?? '',
        category: values.category,
        options: field.options ?? [],
        min: field.min ?? '',
        max: field.max ?? '',
        integerOnly: field.integerOnly ?? false,
        minLabel: field.minLabel ?? '',
        maxLabel: field.maxLabel ?? '',
        maxFileSizeMB: field.maxFileSizeMB ?? '',
        acceptedFileTypes: field.acceptedFileTypes ?? [],
        maxEntries: field.maxEntries ?? '',
      })
      reset()
      onSaved()
      onOpenChange(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save to library'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save to Field Library</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="fieldId">Field ID</Label>
            <Input
              id="fieldId"
              className="font-mono"
              placeholder="applicant-email"
              {...register('fieldId')}
            />
            <p className="text-xs text-muted-foreground">
              Used for answer mapping across forms. Cannot be changed once set.
            </p>
            {errors.fieldId && (
              <p className="text-sm text-destructive">{withErrorCode(String(errors.fieldId.message), 'app/validation')}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              placeholder="General"
              {...register('category')}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save to Library'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
